import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';

import { formatAbsoluteTime, formatRelativeTime } from '@/utils/date';

import { formatDateDDMMYYYY } from '../constants/taxonomy';
import {
  BRIEFS_PAGE_SIZE,
  channelLabel,
  listBriefs,
  summarizeBrief,
  type BriefOut,
  type BriefStatus,
} from './api';

/**
 * One tab's briefs, loaded 20 at a time as the user scrolls.
 *
 * The backend sends no total, so a page shorter than 20 is the end of the
 * list. Scrolling near the bottom loads the next page; the "Load more" button
 * is there for the cases the observer can't cover (a list that fits the
 * screen, keyboard users, browsers without IntersectionObserver).
 */
export function BriefList({
  status,
  channel,
  onOpen,
  showCreatedBy,
  onClearChannel,
}: {
  status: BriefStatus;
  channel: string;
  onOpen: (briefId: string) => void;
  /**
   * Whose brief each row is. Only worth a column where other people's briefs
   * can appear; for everyone else every row would read their own address.
   */
  showCreatedBy: boolean;
  onClearChannel: () => void;
}) {
  const query = useInfiniteQuery({
    // Under ['briefs'], which saving or sending from the basket invalidates,
    // so a brief made there is listed here without a reload.
    queryKey: ['briefs', 'list', { status, channel }],
    queryFn: ({ pageParam }) => listBriefs({ status, channel, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length < BRIEFS_PAGE_SIZE ? undefined : pages.length * BRIEFS_PAGE_SIZE,
  });
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } = query;

  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinel.current;
    // Not after a failed page: the observer would fire again at once and keep
    // retrying a request that just failed. The retry is the user's to make.
    if (
      !node ||
      !hasNextPage ||
      isFetchNextPageError ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
      },
      // Start loading a little before the bottom is reached.
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError]);

  if (query.isPending) {
    return (
      <div className="flex min-h-[320px] items-center justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" aria-label="Loading briefs" />
      </div>
    );
  }

  // Nothing loaded at all. Not `isError`: that is also true when a later page
  // or a background refresh fails, and briefs already on screen must stay.
  if (!query.data) {
    // Distinct from the empty state on purpose: a failed request shown as "no
    // drafts yet" invites someone to redo a brief they already made.
    return (
      <Notice tone="error" title="Couldn’t load your briefs">
        <p>The asset library didn’t respond. This is a loading problem, not an empty list.</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="mt-3 rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3]"
        >
          Try again
        </button>
      </Notice>
    );
  }

  const briefs = query.data.pages.flat();

  if (briefs.length === 0) {
    if (channel) {
      return (
        <Notice title={`No ${channelLabel(channel).toLowerCase()} briefs here`}>
          <button type="button" onClick={onClearChannel} className="text-[#0014cc] hover:underline">
            Show all channels
          </button>
        </Notice>
      );
    }
    return status === 'draft' ? (
      <Notice title="No drafts yet">
        <p>Save a basket as a draft from the asset library to finish it later.</p>
      </Notice>
    ) : (
      <Notice title="No requested briefs yet">
        <p>Briefs you send to production appear here, with their reference.</p>
      </Notice>
    );
  }

  return (
    <div>
      <table className="w-full border-separate border-spacing-0 text-left text-[12.5px]">
        <thead>
          <tr className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {status === 'requested' ? (
              <>
                <Th>Reference</Th>
                <Th>Sent</Th>
              </>
            ) : (
              <Th>Last updated</Th>
            )}
            {showCreatedBy && <Th>Created by</Th>}
            <Th>Channel</Th>
            <Th>Rows</Th>
            <Th>Markets</Th>
            <Th>Assets</Th>
            <Th>Needed by</Th>
            <Th>{status === 'requested' ? 'Outcome' : 'Status'}</Th>
          </tr>
        </thead>
        <tbody>
          {briefs.map((brief) => (
            <BriefRow key={brief.id} brief={brief} showCreatedBy={showCreatedBy} onOpen={onOpen} />
          ))}
        </tbody>
      </table>

      <div ref={sentinel} />
      {isFetchNextPageError && !isFetchingNextPage ? (
        <div
          role="alert"
          className="flex items-center justify-center gap-2 py-4 text-[12.5px] text-red-600"
        >
          Couldn’t load more briefs.
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            className="font-medium text-[#0014cc] hover:underline"
          >
            Try again
          </button>
        </div>
      ) : (
        hasNextPage && (
          <div className="flex justify-center py-4">
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#0014cc] hover:underline disabled:text-slate-400 disabled:no-underline"
            >
              {isFetchingNextPage && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )
      )}
    </div>
  );
}

/**
 * The whole row opens the brief, for the mouse. The first cell is the button,
 * for the keyboard and screen readers, so the row needs no ARIA of its own.
 */
function BriefRow({
  brief,
  showCreatedBy,
  onOpen,
}: {
  brief: BriefOut;
  showCreatedBy: boolean;
  onOpen: (briefId: string) => void;
}) {
  const summary = summarizeBrief(brief);
  const open = () => onOpen(brief.id);
  return (
    <tr
      onClick={open}
      className="cursor-pointer text-slate-700 transition-colors hover:bg-slate-50 [&>td]:border-b [&>td]:border-slate-100 [&>td]:px-3 [&>td]:py-2.5"
    >
      {brief.status === 'requested' ? (
        <>
          <td className="font-mono font-semibold text-[#0014cc]">
            <OpenButton onOpen={open}>{brief.reference ?? '—'}</OpenButton>
          </td>
          {/* Timestamps go through Content Tracker's UTC-aware formatters, so
              the day is the user's, not UTC's. Needed by below is a plain
              date and stays as it is. */}
          <td>{brief.requested_at ? formatAbsoluteTime(brief.requested_at) : '—'}</td>
        </>
      ) : (
        <td>
          <OpenButton onOpen={open}>{formatRelativeTime(brief.updated_at)}</OpenButton>
        </td>
      )}
      {showCreatedBy && (
        <td className="max-w-[200px] truncate" title={brief.created_by}>
          {brief.created_by}
        </td>
      )}
      <td>{channelLabel(brief.channel)}</td>
      <td>{summary.rowCount}</td>
      <td className="max-w-[180px] truncate" title={summary.markets.join(', ')}>
        {summary.markets.join(', ') || '—'}
      </td>
      <td>{summary.lineCount}</td>
      <td>{formatDateDDMMYYYY(brief.needed_by) || '—'}</td>
      <td>
        <BriefOutcome brief={brief} skipped={summary.skippedRowCount} rows={summary.rowCount} />
      </td>
    </tr>
  );
}

/**
 * A partially landed brief must not read as fully sent: a skipped row is a job
 * nobody is doing. A draft whose last submit skipped every line is still a
 * draft, and says why it is still here.
 */
function BriefOutcome({
  brief,
  skipped,
  rows,
}: {
  brief: BriefOut;
  skipped: number;
  rows: number;
}) {
  if (brief.status === 'draft') {
    return skipped > 0 ? (
      <Tag tone="amber">Not sent: see reasons</Tag>
    ) : (
      <Tag tone="slate">Draft</Tag>
    );
  }
  return skipped > 0 ? (
    <Tag tone="amber">
      Partially sent: {rows - skipped} of {rows} rows
    </Tag>
  ) : (
    <Tag tone="green">All sent</Tag>
  );
}

function OpenButton({ onOpen, children }: { onOpen: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        // The row's own click would open it a second time.
        event.stopPropagation();
        onOpen();
      }}
      className="text-left hover:underline focus:underline focus:outline-none"
    >
      {children}
    </button>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="border-b border-slate-200 px-3 pb-2 font-semibold">{children}</th>;
}

const TAG_TONES = {
  slate: 'bg-slate-100 text-slate-600',
  amber: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200',
  green: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
} as const;

function Tag({ tone, children }: { tone: keyof typeof TAG_TONES; children: ReactNode }) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${TAG_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

function Notice({
  title,
  tone = 'neutral',
  children,
}: {
  title: string;
  tone?: 'neutral' | 'error';
  children?: ReactNode;
}) {
  return (
    <div
      className={`flex min-h-[320px] items-center justify-center rounded-lg border border-dashed bg-white ${
        tone === 'error' ? 'border-red-200' : 'border-slate-200'
      }`}
    >
      <div className="max-w-md text-center text-[12.5px] leading-relaxed text-slate-500">
        <h2 className="mb-2 text-[15.5px] font-bold text-slate-800">{title}</h2>
        {children}
      </div>
    </div>
  );
}
